import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import "./AddNewsFormPage.css";

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

export default function AddNewsFormPage() {
  const navigate = useNavigate();

  // ===============================
  // Current User
  // ===============================
  const [currentUser, setCurrentUser] = useState({ name: "" });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setCurrentUser({
        name: user.name || user.fullname || "Admin",
      });
    }
  }, []);

  // ===============================
  // News Categories
  // ===============================
  const newsCategories = [
    "Announcement",
    "Achievement",
    "Alumni Spotlight",
    "Event Update",
    "Academic News",
    "Career Opportunity",
    "Partnership",
    "Research & Innovation",
    "Community Outreach",
    "General News",
  ];

  // ===============================
  // Form State
  // ===============================
  const [formData, setFormData] = useState({
    title: "",
    category: "",
    description: "",
    postedBy: "",
    image: null,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Auto set postedBy
  useEffect(() => {
    if (currentUser.name) {
      setFormData((prev) => ({
        ...prev,
        postedBy: currentUser.name,
      }));
    }
  }, [currentUser]);

  // ===============================
  // Handlers
  // ===============================
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "image") {
      setFormData((prev) => ({
        ...prev,
        image: files[0],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.category || !formData.description) {
      setError("Please fill all required fields.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const data = new FormData();
      data.append("title", formData.title);
      data.append("category", formData.category);
      data.append("description", formData.description);
      data.append("postedBy", formData.postedBy);

      if (formData.image) {
        data.append("image", formData.image);
      }

      const res = await axios.post(`${API_BASE_URL}/news`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      alert(res.data.message || "News created successfully!");
      navigate("/admin-panel/news-page");
    } catch (err) {
      console.error("Failed to create news:", err.response || err.message);
      setError(
        err.response?.data?.message ||
          "Failed to create news. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-news-page">
      <motion.div
        className="add-news-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <button
          type="button"
          className="back-btn"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>

        <h2>Create News</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              name="title"
              placeholder="Enter news title"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Category</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
            >
              <option value="">Select Category</option>
              {newsCategories.map((cat, index) => (
                <option key={index} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              placeholder="Enter news description"
              value={formData.description}
              onChange={handleChange}
              rows={5}
              required
            />
          </div>

          <div className="form-group">
            <label>News Image (Optional)</label>
            <input
              type="file"
              name="image"
              accept="image/*"
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Posted By</label>
            <input
              type="text"
              name="postedBy"
              value={formData.postedBy}
              disabled
            />
          </div>

          {error && <div className="error-msg">{error}</div>}

          <motion.button
            type="submit"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            disabled={loading}
            className="submit-btn"
          >
            {loading ? "Creating..." : "Create News"}
          </motion.button>
        </form>
      </motion.div>
    </div>
  );
}